/**
 * The team post page's comments, where the layout puts them (library
 * Calendar / Comments rail, Comments · Tablet, Comments · Phone): on the desk,
 * the rail's Comments tab (the page portals into the slot the rail
 * publishes); on a tablet, a 380 sheet 8px in from the right, radius 16, over
 * a scrim; on a phone, a 16 radius sheet 8px in from the sides and the foot,
 * 56 from the top, with a grabber. The data and the posting live in
 * usePostComments; this file places them and wires the acts.
 */
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, MessageSquare, PanelRightClose } from "lucide-react";
import { EmptyState } from "../../components/ViewStates";
import { KitModal } from "../../components/kit/Modal";
import RailTabs from "../../agent/rail/RailTabs";
import { useAgentPanelStore } from "../../agent/agentPanelStore";
import { useRailComments } from "../../agent/railCommentsStore";
import type { PostViewport } from "../components/StatusBanner";
import type { PostPhase } from "../components/PostHeader";
import CommentThread from "./CommentThread";
import CommentComposer, { type CommentComposerHandle } from "./CommentComposer";
import { shortDate } from "../postState";
import { commentsClosedLine, composerSource, partLabel, teamRailSubtitle, type CommentThreadData } from "./commentModel";
import { anchorElement, useCommentAnchorStore } from "./commentAnchorStore";
import { useMentionPeople } from "./useMentionPeople";
import type { PostCommentsData } from "./usePostComments";
import "./comments.css";

type Phase = PostPhase;

/** A key press meant for a field, a menu or a dialog, not for the page. */
function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el?.closest?.("input, textarea, select, [contenteditable='true'], [role='menu'], [role='listbox'], [role='dialog']");
}

/** Three cards shaped like threads while the first read is out. */
function ThreadSkeletons() {
  return (
    <ul className="co-cmt-list" aria-hidden="true">
      {[0, 1, 2].map((index) => (
        <li key={index} className="co-cmt-thread co-cmt-thread--skeleton">
          <span className="co-cmt-sk" style={{ inlineSize: "46%" }} />
          <span className="co-cmt-sk" style={{ inlineSize: "34%" }} />
          <span className="co-cmt-sk is-body" />
          <span className="co-cmt-sk is-body" style={{ inlineSize: "72%" }} />
        </li>
      ))}
    </ul>
  );
}

function focusSoon(composer: RefObject<CommentComposerHandle | null>) {
  // Two frames: one for the rail or sheet to mount, one for its field.
  requestAnimationFrame(() => requestAnimationFrame(() => composer.current?.focus()));
}

function CommentsPanel({
  data,
  clientName,
  phase,
  briefExists,
  briefShared,
  phone,
  tabPanel,
  slotId,
  onOpenRail,
}: {
  data: PostCommentsData;
  clientName: string | null;
  phase: Phase;
  briefExists: boolean;
  /** The client already has the brief, so they read its comments. */
  briefShared: boolean;
  phone: boolean;
  /** Sheets only: there the panel is the Comments tab's. On the desk the
   *  rail's slot already is that tabpanel, so the panel is a plain div. */
  tabPanel?: { id: string; labelledBy: string };
  slotId: string;
  /** Opens the comments wherever they live (the rail tab or the sheet). */
  onOpenRail: () => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const composer = useRef<CommentComposerHandle>(null);
  const client = clientName?.trim() || null;
  const people = useMentionPeople(client, data.canComment);
  const source = composerSource(phase, briefExists);
  const closedLine = data.closed ? commentsClosedLine(client, null, data.closedBy) : null;
  const request = useCommentAnchorStore((s) => s.request);
  const clearRequest = useCommentAnchorStore((s) => s.clear);
  const anchor = request && !data.closed ? request : null;
  const writeSource = anchor?.source ?? source;
  const disabledReason =
    writeSource === "concept"
      ? data.conceptTarget
        ? null
        : data.conceptApproved
          ? `${client ?? "The client"} approved the concept.`
          : data.hasSharedConcept
            ? "Concept comments are closed on this version."
            : `Comments open once the concept is sent to ${client ?? "the client"}.`
      : data.briefTarget
        ? null
        : "The creative brief is not ready for comments yet.";

  const openRail = useRef(onOpenRail);
  useEffect(() => {
    openRail.current = onOpenRail;
  });

  // A part asked to be commented on: open, and put the caret in the field.
  const requestAt = request?.at;
  useEffect(() => {
    if (!requestAt) return;
    openRail.current();
    focusSoon(composer);
  }, [requestAt]);

  // "c" comments, as the hint under the field says.
  const canWrite = data.canComment && !data.closed;
  useEffect(() => {
    if (!canWrite) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "c" || event.metaKey || event.ctrlKey || event.altKey || event.defaultPrevented) return;
      if (isTyping(event.target)) return;
      event.preventDefault();
      openRail.current();
      focusSoon(composer);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canWrite]);

  const showOnPage = (t: CommentThreadData) => {
    if (!t.anchor) return;
    const el = anchorElement(t.source, t.anchor.part, t.anchorKey);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.setAttribute("data-comment-flash", "true");
    window.setTimeout(() => el.removeAttribute("data-comment-flash"), 1600);
  };

  const thread = (t: CommentThreadData) => {
    const rootId = t.id.startsWith("local:") ? null : t.comment.id;
    const draft = t.m7Change?.id ?? null;
    const canAct = data.canComment && !!rootId;
    return (
      <li key={t.id}>
        <CommentThread
          thread={t}
          clientName={client}
          now={data.now}
          open={openId === t.id}
          onSelect={() => setOpenId((current) => (current === t.id ? null : t.id))}
          onRetry={t.comment.sendState === "failed" ? () => data.retry(t.id) : undefined}
          onRetryReply={(entry) => data.retry(entry.id)}
          phone={phone}
          userId={data.userId}
          authorName={data.authorName}
          people={people}
          onToggleDone={canAct && rootId ? () => data.actions.setDone(t.source, rootId, !t.resolvedAt) : undefined}
          onReply={
            canAct && !data.closed && rootId
              ? (body, mentions) => data.post(t.source, body, { parentId: rootId, targetId: t.targetId, mentions })
              : undefined
          }
          onShowOnPage={t.anchor ? () => showOnPage(t) : undefined}
          onEdit={data.canComment ? (entry, body) => data.actions.edit(t.source, entry.id, body) : undefined}
          onDelete={data.canComment ? (entry) => data.actions.remove(t.source, entry.id) : undefined}
          onAcceptChange={canAct && draft ? () => data.actions.decideDraft(draft, "accept") : undefined}
          onDismissChange={canAct && draft ? () => data.actions.decideDraft(draft, "dismiss") : undefined}
          onUndoChange={canAct && draft ? () => data.actions.decideDraft(draft, "undo") : undefined}
        />
      </li>
    );
  };

  // Once comments are closed every thread stays in view, done or not.
  const listed = data.closed ? [...data.open, ...data.done] : data.open;
  const folded = data.closed ? [] : data.done;

  return (
    <div
      className="co-cmt-panel"
      {...(tabPanel ? { id: tabPanel.id, role: "tabpanel", "aria-labelledby": tabPanel.labelledBy } : {})}
    >
      {data.loading ? (
        <ThreadSkeletons />
      ) : data.threads.length === 0 ? (
        <div className="co-cmt-empty">
          <EmptyState
            minHeight={240}
            icon={<MessageSquare size={20} strokeWidth={1.5} aria-hidden="true" />}
            title="No comments yet"
            body={
              // A closed composer says why under itself; the empty state must
              // not invite a comment the field will not take.
              data.canComment && !disabledReason && !closedLine
                ? `Comment on any part of the page. ${client ?? "The client"} sees every comment.`
                : "Nothing to discuss yet."
            }
          />
        </div>
      ) : (
        <ul className="co-cmt-list" aria-label="Comments">
          {listed.map(thread)}
          {folded.length > 0 ? (
            <li>
              <details className="co-cmt-fold">
                <summary>Done · {folded.length}</summary>
                <ul className="co-cmt-list co-cmt-list--nested">{folded.map(thread)}</ul>
              </details>
            </li>
          ) : null}
        </ul>
      )}
      {data.canComment ? (
        <CommentComposer
          ref={composer}
          key={slotId}
          source={writeSource}
          authorName={data.authorName}
          clientName={client}
          sharedNote={writeSource === "brief" && briefShared ? `${client ?? "The client"} sees brief comments` : null}
          disabledReason={disabledReason}
          closedLine={closedLine}
          part={
            anchor
              ? { label: `${anchor.source === "concept" ? "Concept" : "Creative brief"} · ${partLabel(anchor.label)}`, onClear: clearRequest }
              : null
          }
          phone={phone}
          people={people}
          onPost={(body, mentions) =>
            data.post(writeSource, body, {
              anchor: anchor ? { part: anchor.part, key: anchor.key ?? null, label: anchor.label } : null,
              mentions,
            })
          }
        />
      ) : null}
    </div>
  );
}

const DRAG_SLOP = 4;
const DRAG_CLOSE = 64;

/** Phone: the sheet drags down from its head to close. */
function usePhoneDrag(onClose: () => void) {
  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0 || (event.target as HTMLElement).closest("button")) return;
    const sheet = event.currentTarget.closest<HTMLElement>(".co-cmt-sheet--phone");
    if (!sheet) return;
    const startY = event.clientY;
    let dy = 0;
    const move = (e: PointerEvent) => {
      dy = e.clientY - startY;
      if (Math.abs(dy) < DRAG_SLOP) return;
      sheet.dataset.dragging = "true";
      sheet.style.transform = dy > 0 ? `translate3d(0, ${dy}px, 0)` : "";
    };
    const end = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", cancel);
      delete sheet.dataset.dragging;
      sheet.style.transform = "";
      if (dy >= DRAG_CLOSE) onClose();
    };
    const cancel = () => {
      dy = 0;
      end();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", cancel);
  };
  return { onPointerDown };
}

export default function PostComments({
  data,
  enabled,
  viewport,
  clientName,
  phase,
  briefExists,
  brief,
  slotId,
  sheetOpen,
  onSheetOpenChange,
}: {
  data: PostCommentsData;
  /** Team only; the client's pages keep theirs. */
  enabled: boolean;
  viewport: PostViewport;
  clientName: string | null;
  phase: Phase;
  briefExists: boolean;
  brief: { version: number; sentVersion: number | null } | null;
  slotId: string;
  sheetOpen: boolean;
  onSheetOpenChange: (open: boolean) => void;
}) {
  const subtitle = teamRailSubtitle({
    brief,
    clientName,
    drafts: data.open.filter((thread) => !!thread.m7Change).length,
    threads: data.open.length,
    closed: data.closed ? { date: shortDate(data.closedAt), by: data.closedBy } : null,
  });
  const slot = useRailComments(enabled ? { count: data.count, subtitle } : null);
  const close = () => onSheetOpenChange(false);
  const open = () => onSheetOpenChange(true);
  const phoneDrag = usePhoneDrag(close);
  if (!enabled) return null;

  const briefShared = !!brief?.sentVersion;
  const phone = viewport === "phone";

  if (viewport === "desktop") {
    return slot
      ? createPortal(
          <CommentsPanel
            data={data}
            clientName={clientName}
            phase={phase}
            briefExists={briefExists}
            briefShared={briefShared}
            phone={false}
            slotId={slotId}
            onOpenRail={open}
          />,
          slot,
        )
      : null;
  }

  // Chat with M7 in a sheet hands over to the agent's own sheet.
  const openChat = () => {
    close();
    useAgentPanelStore.getState().setOpen(true);
  };

  return (
    <KitModal
      isOpen={sheetOpen}
      onOpenChange={onSheetOpenChange}
      aria-label="Comments"
      hideCloseButton
      overlayClassName={phone ? "co-cmt-sheet-overlay--phone" : "co-cmt-sheet-overlay--tablet"}
      modalClassName={phone ? "co-cmt-sheet co-cmt-sheet--phone" : "co-cmt-sheet co-cmt-sheet--tablet"}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {phone ? (
          <span className="co-cmt-sheet-grabber" aria-hidden="true" onPointerDown={phoneDrag.onPointerDown} />
        ) : null}
        <RailTabs
          tab="comments"
          onTab={(tab) => {
            if (tab === "chat") openChat();
          }}
          count={data.count}
          subtitle={subtitle}
          idPrefix="co-cmt-sheet"
          chatPanel={false}
          onPointerDown={phone ? phoneDrag.onPointerDown : undefined}
          collapse={
            <button
              type="button"
              className="co-railtabs-collapse"
              aria-label="Close comments"
              title="Close"
              onClick={close}
            >
              {phone ? (
                <ChevronDown aria-hidden strokeWidth={1.75} />
              ) : (
                <PanelRightClose aria-hidden strokeWidth={1.75} />
              )}
            </button>
          }
        />
        <CommentsPanel
          data={data}
          clientName={clientName}
          phase={phase}
          briefExists={briefExists}
          briefShared={briefShared}
          phone={phone}
          tabPanel={{ id: "co-cmt-sheet-panel-comments", labelledBy: "co-cmt-sheet-tab-comments" }}
          slotId={slotId}
          onOpenRail={open}
        />
      </div>
    </KitModal>
  );
}
